import { makeAuthenticatedRequest } from './api';

// Admin Email Monitor API wrappers. Backend routes are admin-gated (authMiddleware('admin')),
// POST-with-JSON-body, mounted at /email-monitor. See jims-mowing-api/src/routes/emailMonitor.routes.js.
// makeAuthenticatedRequest already unwraps `data`, so these resolve to the payload itself.

/**
 * Monitored emails, newest first.
 * @param {object} filters
 * @param {string|string[]|null} filters.status   - status(es) to include; null for all.
 * @param {string|null} filters.mailbox           - which inbox the email arrived in.
 * @param {string|null} filters.criteria          - legacy glued label; prefer senderGroup/issueFlag.
 * @param {string|null} filters.senderGroup       - franchisee | supplier | other (who sent it).
 * @param {string|null} filters.issueFlag         - issue | enquiry | other (what it is about).
 * @param {number|null} filters.sinceHours        - only emails received within this window.
 * @returns {Promise<{enabled: boolean, emails: Array}>} enabled is false when the API has the monitor switched off.
 */
export const listMonitoredEmails = async ({ status = null, mailbox = null, criteria = null, senderGroup = null, issueFlag = null, sinceHours = null, limit = 200 } = {}) => {
  const data = await makeAuthenticatedRequest('/email-monitor/list', {
    method: 'POST',
    body: { status, mailbox, criteria, senderGroup, issueFlag, sinceHours, limit },
  });
  return { enabled: !!data?.enabled, emails: data?.emails || [] };
};

// Saves an edited draft; moves a needs_review item to drafted.
// Pass draftFaqIds: [] to drop the knowledge citation (e.g. admin rewrote the reply).
export const updateDraft = async (id, draftText, draftFaqIds) =>
  makeAuthenticatedRequest('/email-monitor/update-draft', {
    method: 'POST',
    body: draftFaqIds === undefined ? { id, draftText } : { id, draftText, draftFaqIds },
  });

// Sends a reply for each id (Graph reply action). Returns { sent, total, results:[{id,ok,error?}] }.
export const sendReplies = async (ids) =>
  makeAuthenticatedRequest('/email-monitor/send', {
    method: 'POST',
    body: { ids },
  });

/**
 * The full conversation an email belongs to, read live from the mailbox.
 * @returns {Promise<{messages: Array, threadUnavailable?: boolean}>}
 */
export const getEmailThread = async (id) =>
  makeAuthenticatedRequest('/email-monitor/thread', { method: 'POST', body: { id } });

/**
 * Polls the mailboxes now rather than waiting for the 5-minute schedule. Slow by nature — it is doing
 * the real Graph round trip — so callers should show progress.
 */
export const refreshMonitoredEmails = async () =>
  makeAuthenticatedRequest('/email-monitor/refresh', { method: 'POST', body: {} });

// { allowedSenders, excludedSenders, autoDraftReply, autoSendReply, lookbackHours, mailboxes }
export const getEmailMonitorSettings = async () =>
  makeAuthenticatedRequest('/email-monitor/settings', { method: 'POST', body: {} });

// Every field is optional; anything omitted keeps its stored value.
export const updateEmailMonitorSettings = async (settings) =>
  makeAuthenticatedRequest('/email-monitor/settings/update', { method: 'POST', body: settings });

// One-click allow-listing from the thread panel. Only affects future mail.
export const allowSender = async (email) =>
  makeAuthenticatedRequest('/email-monitor/allow-sender', { method: 'POST', body: { email } });

// A gift voucher email that needs a person: try making the RCTI again after fixing the cause.
// Resolves to { id, status, giftVoucher }; rejects with the reason when it still cannot be made.
export const retryGiftVoucher = async (id) =>
  makeAuthenticatedRequest('/email-monitor/gift-voucher/retry', { method: 'POST', body: { id } });

// Record that the RCTI for a voucher email was made by hand, so it leaves the "To answer" list.
export const markGiftVoucherHandled = async (id) =>
  makeAuthenticatedRequest('/email-monitor/gift-voucher/handled', { method: 'POST', body: { id } });

// Knowledge base behind FAQ-grounded drafts (max ~60 entries; one fetch lists them all).
export const listKnowledgeEntries = async (activeOnly = false) => {
  const data = await makeAuthenticatedRequest('/email-monitor/knowledge/list', { method: 'POST', body: { activeOnly } });
  return data?.entries || [];
};

export const saveKnowledgeEntry = async (entry, id = null) =>
  makeAuthenticatedRequest('/email-monitor/knowledge/save', { method: 'POST', body: { id, entry } });

export const setKnowledgeActive = async (id, isActive) =>
  makeAuthenticatedRequest('/email-monitor/knowledge/set-active', { method: 'POST', body: { id, isActive } });

// Dry run: which entry (if any) would ground a draft for this text. Reads only.
export const testKnowledgeMatch = async ({ subject = '', body = '', senderGroup = 'franchisee', intent = 'general_inquiry' }) =>
  makeAuthenticatedRequest('/email-monitor/knowledge/test-match', { method: 'POST', body: { subject, body, senderGroup, intent } });
